package com.iregained.site.components.widgets

import androidx.compose.runtime.*
import com.iregained.site.theme.HeadlineTextStyle
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Inner Staggered Float
@Composable
private fun Modifier.innerFloat(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 20.px).transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun SharedCtaWidget(
    headline: String, description: String, actions: @Composable () -> Unit
) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    var isVisible by remember { mutableStateOf(false) }

    if (headline.isNotBlank()) {
        Box(
            modifier = Modifier.fillMaxWidth().backgroundColor(palette.brand.primary)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
            contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Div(
                        attrs = HeadlineTextStyle.toModifier().color(Colors.White)
                            .fontSize(if (isDesktop) 36.px else 28.px)
                            .fontWeight(FontWeight.Bold).textAlign(TextAlign.Center).margin(bottom = 24.px)
                            .innerFloat(isVisible, 0).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = headline
                                    onDispose {}
                                }
                            }
                    )

                    if (description.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().fontSize(20.px)
                                .color(Colors.White.copyf(alpha = 0.9f)).textAlign(TextAlign.Center)
                                .margin(bottom = 40.px).lineHeight(1.6).innerFloat(isVisible, 150).toAttrs {
                                    ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} }
                                })
                    }

                    Row(
                        modifier = Modifier.gap(20.px).flexWrap(FlexWrap.Wrap).innerFloat(isVisible, 300),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        actions()
                    }
                }
            }
        }
    }
}

@Composable
fun SharedCtaActionLink(text: String, path: String, styleModifier: Modifier) {
    val palette = ColorMode.current.toSitePalette()

    if (text.isNotBlank() && path.isNotBlank()) {
        Link(path = path, modifier = Modifier.textDecorationLine(TextDecorationLine.None)) {
            Box(
                modifier = styleModifier.padding(leftRight = 40.px, topBottom = 16.px).borderRadius(8.px)
                    .cursor(Cursor.Pointer), contentAlignment = Alignment.Center
            ) {
                SpanText(
                    text = text,
                    modifier = Modifier.fontSize(20.px).color(palette.brand.primary).fontWeight(FontWeight.Bold)
                )
            }
        }
    }
}